export interface AuthStrategy {
  name: string;
  description: string;
  type: 'cookie' | 'bearer' | 'firebase_token' | 'none';
  loginPath: string;
  extraFields: Record<string, string>;
  bodyFieldMapping: Record<string, string>;
  credentials_masked: Record<string, string>;
  isDefault: boolean;
}

export interface ServiceProfile {
  _id: string;
  repo: string;
  displayName: string;
  description: string;
  previewUrl: string;
  baseBranch: string;
  auth: {
    type: 'cookie' | 'bearer' | 'firebase_token' | 'none';
    loginPath: string;
    extraFields: Record<string, string>;
    bodyFieldMapping: Record<string, string>;
    credentials_masked: Record<string, string>;
  };
  authStrategies: AuthStrategy[];
  techStack: {
    framework: string;
    uiLibrary: string;
    designSystem: string;
    designSystemFigmaKey: string;
  };
  userPersonas: Array<{
    name: string;
    description: string;
    primaryFlows: string[];
  }>;
  designGuidelines: string;
  designSystemMd: string;
  unblockedContext?: {
    lastFetched: string | null;
    ttlHours: number;
    cachedAnswers: Array<{
      question: string;
      aspect: string;
      answer: string;
      references: Array<{
        htmlUrl: string;
        sourceType: string;
        title?: string;
        snippet?: string;
      }>;
      fetchedAt: string;
    }>;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// Redesign types
export type RedesignStatus = 'pending' | 'capturing' | 'captured' | 'analyzing' | 'analyzed' | 'failed';
export type TaskStatus = 'pending' | 'redesigning' | 'complete' | 'failed';

export interface RedesignVariant {
  variantIndex: number;
  label: string;
  imageUrl: string;
  codeUrl: string;
  selected: boolean;
}

export interface RedesignPage {
  route: string;
  viewport: string;
  title: string;
  originalScreenshotUrl: string;
  screenId: string;
  comment?: string;
  excluded?: boolean;
  status: 'pending' | 'complete' | 'failed' | 'skipped';
  variants: RedesignVariant[];
  codeExport?: {
    html: string;
    css: string;
    react?: string;
    tailwind?: string;
  };
  visionAnalysis?: string;
  geminiStatus?: 'pending' | 'success' | 'failed';
  geminiError?: string;
  figmaFileKey?: string;
  figmaNodeId?: string;
  figmaContext?: string;
  figmaScreenshotUrl?: string;
  figmaImports?: Array<{
    fileKey: string;
    nodeId: string;
    screenshotUrl?: string;
    label?: string;
  }>;
  figmaStatus?: 'pending' | 'success' | 'failed';
  figmaError?: string;
  error?: string;
}

export interface DesignSystemMeta {
  tokenCount?: number;
  lastSyncedAt?: string;
  propagationStatus?: 'idle' | 'in_progress' | 'complete' | 'failed';
  affectedRedesignIds?: string[];
}

export interface Redesign {
  _id: string;
  name?: string;
  repo: string;
  serviceProfileId: string;
  prompt: string;
  mode?: RedesignMode;
  designSource?: 'screenshots' | 'figma' | 'both';
  referenceRoutes?: string[];
  status: RedesignStatus;
  pages: RedesignPage[];
  stats: {
    totalPages: number;
    captured: number;
    failed: number;
    excluded: number;
  };
  designSystemMeta?: DesignSystemMeta;
  error?: string;
  taskSummary?: {
    totalTasks: number;
    completedTasks: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface RedesignTask {
  _id: string;
  redesignId: string;
  name: string;
  designerNotes: string;
  expandedDesignerNotes?: string;
  pageRef: { route: string; viewport: string };
  stitchProjectId: string;
  stitchProjectName: string;
  figmaSource?: { fileKey: string; nodeId: string };
  figmaDesign?: {
    fileKey: string;
    nodeId: string;
    pageUrl: string;
    submittedAt: string;
    completedAt?: string;
    status: 'pending' | 'complete' | 'failed';
    error?: string;
  };
  figmaWriteBack?: { fileKey: string; nodeId: string; pushedAt: string };
  page: RedesignPage | null;
  status: TaskStatus;
  designerIssue?: {
    linearIssueId: string;
    linearIssueUrl: string;
    linearIdentifier: string;
    createdAt: string;
  };
  implementationIssue?: {
    linearIssueId: string;
    linearIssueUrl: string;
    linearIdentifier: string;
    createdAt: string;
  };
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export const REDESIGN_STATUS_COLORS: Record<RedesignStatus, string> = {
  pending: '#78716c',
  capturing: '#ffc107',
  captured: '#ffc107',
  analyzing: '#8b5cf6',
  analyzed: '#8b5cf6',
  failed: '#dc3545',
};

export const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  pending: '#78716c',
  redesigning: '#6366f1',
  complete: '#198754',
  failed: '#dc3545',
};

// Component catalog types
export type ComponentCategory = 'action' | 'input' | 'layout' | 'navigation' | 'feedback' | 'data' | 'media' | 'composite';
export type UnificationStatus = 'cataloged' | 'designing' | 'generated' | 'published' | 'migrating' | 'complete';

export interface AppImplementation {
  repo: string;
  filePath: string;
  componentName: string;
  framework: string;
  variants: string[];
  hasLightMode: boolean;
  hasDarkMode: boolean;
  hasRoleAccents: boolean;
  screenshotUrl?: string;
  notes?: string;
}

export interface MigrationStatus {
  repo: string;
  status: 'pending' | 'in_progress' | 'complete' | 'skipped';
  prUrl?: string;
  migratedAt?: string;
}

export interface ComponentEntryData {
  _id: string;
  name: string;
  category: ComponentCategory;
  description: string;
  implementations: AppImplementation[];
  unified?: {
    packagePath: string;
    exportName: string;
    variants: string[];
    sizes: string[];
    states: string[];
    generatedAt?: string;
    publishedAt?: string;
  };
  migrations: MigrationStatus[];
  unificationStatus: UnificationStatus;
  priority: number;
  dependsOn: string[];
  dependedOnBy: string[];
}

// Project types (unified view of packs + redesigns)
export type ProjectType = 'redesign';
export type RedesignMode = 'redesign' | 'feature' | 'greenfield' | 'design_system';

export interface ProjectSummary {
  id: string;
  type: ProjectType;
  title: string;
  repo: string;
  status: string;
  pageCount: number;
  taskSummary?: { totalTasks: number; completedTasks: number };
  thumbnailUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export const UNIFICATION_STATUS_COLORS: Record<UnificationStatus, string> = {
  cataloged: '#78716c',
  designing: '#ffc107',
  generated: '#6366f1',
  published: '#0d6efd',
  migrating: '#8b5cf6',
  complete: '#198754',
};
